/**
 * Express consent to immediate fulfillment for digital content (FAGG).
 *
 * Gates the add-to-cart button of product forms that contain a
 * <purchase-consent> element (rendered by snippets/purchase-consent-checkbox.liquid)
 * and blocks the cart checkout buttons flagged by the cart sections when a
 * course item is in the cart without the consent line item property.
 *
 * The theme re-enables add-to-cart buttons on variant changes
 * (product-info.js toggleAddButton/enableButtons), so the lock is re-applied
 * through a MutationObserver on the button's disabled attribute.
 */
if (!customElements.get('purchase-consent')) {
  class PurchaseConsent extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('input[type="checkbox"]');
      this.hint = this.querySelector('.purchase-consent__hint');
      this.form = this.closest('form');
      if (!this.checkbox || !this.form) return;

      this.submitButton = this.form.querySelector('[type="submit"][name="add"]');
      this.checkbox.addEventListener('change', this.handleChange);

      if (this.submitButton) {
        this.observer = new MutationObserver(this.applyState);
        this.observer.observe(this.submitButton, { attributes: true, attributeFilter: ['disabled'] });
      }

      this.applyState();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    get consentGiven() {
      return this.checkbox.checked;
    }

    refreshTimestamp() {
      this.checkbox.value = `Ja – bestätigt am ${new Date().toISOString()}`;
    }

    showHint() {
      this.hint?.removeAttribute('hidden');
      this.checkbox.focus();
    }

    handleChange = () => {
      if (this.consentGiven) {
        this.refreshTimestamp();
        this.hint?.setAttribute('hidden', '');
      }
      this.applyState();
    };

    applyState = () => {
      if (!this.submitButton) return;

      if (this.consentGiven) {
        if (this.submitButton.hasAttribute('data-consent-locked')) {
          this.submitButton.removeAttribute('data-consent-locked');
          this.submitButton.removeAttribute('disabled');
        }
      } else if (!this.submitButton.hasAttribute('disabled')) {
        this.submitButton.setAttribute('data-consent-locked', '');
        this.submitButton.setAttribute('disabled', 'disabled');
      }
    };
  }
  customElements.define('purchase-consent', PurchaseConsent);

  // Safety net: block any submit of a product form whose consent checkbox is
  // unticked, on every render path (theme ajax, native submit on cart-type
  // pages, quick view). Runs in the capture phase, before the theme's own
  // ProductForm submit handler.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      const consentElement = form.querySelector?.('purchase-consent');
      if (consentElement) {
        if (!consentElement.consentGiven) {
          event.preventDefault();
          event.stopImmediatePropagation();
          consentElement.showHint();
        } else {
          consentElement.refreshTimestamp();
        }
        return;
      }

      // Cart checkout guard: the cart sections render the checkout button
      // disabled with data-consent-blocked when a course item lacks the
      // consent property. Belt and braces in case the disabled state is lost.
      const submitter = event.submitter;
      if (submitter && submitter.name === 'checkout' && submitter.hasAttribute('data-consent-blocked')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );
}
